import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { API_BASE_URL } from "../../../js/api.js";
import { toDateInputValue } from "../../../js/dateHelpers.js";
import {
  FaHandPointer,
  FaHandshake,
  FaEye,
  FaClipboardCheck,
  FaArrowLeft,
} from "react-icons/fa";
import ToastContainer from "../../components/Toast/ToastContainer.js";
import useToast from "../../hooks/useToast.js";
import useConfirm from "../../hooks/useConfirm.js";
import "./style.css";

const formatarDataBR = (dataStr) => {
  if (!dataStr) return "-";
  const d = new Date(dataStr);
  if (isNaN(d.getTime())) return dataStr;
  return d.toLocaleDateString("pt-BR");
};

// Formata uma data já em formato `yyyy-MM-dd` (ou que começa com isso)
// SEM passar por `new Date(...)` — evita conversão por timezone que
// poderia mostrar a data do dia anterior (UTC midnight em fusos
// negativos) ou horário no timestamp. Exibe apenas `dd/mm/aaaa`.
const formatarDataApenas = (dataStr) => {
  if (!dataStr) return "-";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dataStr));
  if (!match) return dataStr;
  return `${match[3]}/${match[2]}/${match[1]}`;
};

const formatarDataHoraBR = (dataStr) => {
  if (!dataStr) return "-";
  const d = new Date(dataStr);
  if (isNaN(d.getTime())) return dataStr;
  return d.toLocaleString("pt-BR");
};

// Formata data/hora no padrão curto `dd/mm/aaaa HH:mm` (sem segundos),
// conforme exemplo da Trello: 16/11/2026 14:35.
// Usa `toLocaleString` com opções explícitas (em vez de `new Date().toLocaleString("pt-BR")`
// puro) para garantir que o formato é sempre `dd/mm/aaaa HH:mm`, não `dd/mm/aaaa, HH:mm:ss`.
const formatarDataHoraCurta = (dataStr) => {
  if (!dataStr) return "-";
  const d = new Date(dataStr);
  if (isNaN(d.getTime())) return dataStr;
  return d.toLocaleString("pt-BR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

// Retorna a data/hora em que o cadastro foi concluído pela empresa
// ou pelo estudante e encaminhado para validação do CIEE.
// Lê com tolerância a variações de nomenclatura no payload e cai
// em `created_at` como último recurso (não deve ficar vazio mesmo se
// o backend ainda não expuser o campo de domínio).
const dataConclusaoItem = (item) => {
  if (!item) return null;
  return (
    item.data_conclusao ??
    item.dataConclusao ??
    item.data_encaminhamento ??
    item.dataEncaminhamento ??
    item.dt_conclusao_cadastro ??
    item.dtConclusaoCadastro ??
    item.created_at ??
    item.createdAt ??
    null
  );
};

const formatarCnpjCpf = (valor) => {
  if (!valor) return "-";
  const nums = String(valor).replace(/\D/g, "").slice(0, 14);
  if (nums.length === 0) return "-";
  if (nums.length <= 11) {
    return nums
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  return nums
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

const verificarPermissao = () => {
  const token = localStorage.getItem("token");
  if (!token) return false;
  try {
    const decoded = jwtDecode(token);
    return decoded.tipo === "usuario_ciee";
  } catch {
    return false;
  }
};

const Validacoes = () => {
  const navigate = useNavigate();
  const {
    toasts,
    error: toastError,
    success: toastSuccess,
    removeToast,
  } = useToast();
  const { confirm, confirmDialog } = useConfirm();
  const [abaAtiva, setAbaAtiva] = useState("novos");
  const [novos, setNovos] = useState([]);
  const [minhas, setMinhas] = useState([]);
  const [loadingNovos, setLoadingNovos] = useState(false);
  const [loadingMinhas, setLoadingMinhas] = useState(false);

  const PAGE_SIZE_OPTIONS = [5, 10, 25, 50];
  const [tamanhoPagina, setTamanhoPagina] = useState(10);
  const [paginaNovos, setPaginaNovos] = useState(1);
  const [paginaMinhas, setPaginaMinhas] = useState(1);

  const recortarPagina = (lista, pagina, tamanho) => {
    const total = lista.length;
    const totalPaginas = Math.max(1, Math.ceil(total / tamanho));
    const paginaSegura = Math.min(Math.max(1, pagina), totalPaginas);
    const inicio = (paginaSegura - 1) * tamanho;
    const itens = lista.slice(inicio, inicio + tamanho);
    return { itens, totalPaginas, total, paginaSegura };
  };

  const ajustarPagina = (lista, pagina, tamanho) => {
    const { paginaSegura } = recortarPagina(lista, pagina, tamanho);
    return paginaSegura;
  };

  const token = localStorage.getItem("token");
  const authHeader = { Authorization: `Bearer ${token}` };

  // Classifica o documento usando APENAS o `_origem` anotado
  // pelo wrapper (TCA / TCE / TCA-Empregador baseado no endpoint
  // que respondeu). Não lê campo do payload — evita a regra
  // frágil "tem cpf? então é TCE" que confundia TCA com TCE.
  const tipoDoc = (item) => {
    if (!item) return "—";
    const origem = item?._origem;
    if (origem === "TCA-Empregador") return "TCA Empregador";
    if (origem === "TCA") return "TCA";
    if (origem === "TCE") return "TCE";
    return "—";
  };

  const rotaDoc = (item) => {
    if (!item) return "/tca";
    const origem = item?._origem;
    if (origem === "TCA-Empregador")
      return `/tca-empregador?id=${item.id}`;
    if (origem === "TCE") return `/tce?id=${item.id}`;
    if (origem === "TCA") return `/tca?id=${item.id}`;
    return `/tca?id=${item.id}`;
  };

  // Endpoint HTTP de atribuir/liberar — espelha a origem.
  const endpointAtribuir = (item) => {
    if (!item) return "/tca/validacoes/atribuir/";
    const origem = item?._origem;
    if (origem === "TCA-Empregador")
      return `/tca-empregador/validacoes/atribuir/${item.id}`;
    if (origem === "TCE") return `/tce/validacoes/atribuir/${item.id}`;
    return `/tca/validacoes/atribuir/${item.id}`;
  };

  const endpointLiberar = (item) => {
    if (!item) return "/tca/validacoes/liberar/";
    const origem = item?._origem;
    if (origem === "TCA-Empregador")
      return `/tca-empregador/validacoes/liberar/${item.id}`;
    if (origem === "TCE") return `/tce/validacoes/liberar/${item.id}`;
    return `/tca/validacoes/liberar/${item.id}`;
  };

  const deduplicar = (lista) => {
    const seen = new Set();
    const out = [];
    for (const item of lista) {
      const key = `${tipoDoc(item)}-${item.id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      out.push(item);
    }
    return out;
  };

  const safeGetNovos = async (path, label) => {
    try {
      const r = await axios.get(`${API_BASE_URL}${path}`, {
        headers: authHeader,
      });
      const lista = Array.isArray(r.data) ? r.data : [];
      // Anota cada item com a origem (endpoint) — fonte de verdade
      // pra classificação/rota/endpoints posteriores.
      const data = lista.map((it) => ({ ...it, _origem: label }));
      return { ok: true, label, data };
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      console.error(
        `[carregarNovos] ${label} (${path}) falhou: status=${status}`,
        data || err?.message,
      );
      return { ok: false, label, status, data };
    }
  };

  const carregarNovos = async () => {
    setLoadingNovos(true);
    try {
      const [tca, tce, tcaEmp] = await Promise.all([
        safeGetNovos("/tca/validacoes/novos", "TCA"),
        safeGetNovos("/tce/validacoes/novos", "TCE"),
        safeGetNovos("/tca-empregador/validacoes/novos", "TCA-Empregador"),
      ]);

      const falhas = [tca, tce, tcaEmp].filter((r) => !r.ok);
      if (falhas.length) {
        console.error(
          "[carregarNovos] Endpoints com falha:",
          falhas.map((f) => `${f.label}=${f.status}`).join(", "),
        );
      }

      const ok = [tca, tce, tcaEmp].filter((r) => r.ok).map((r) => r.data);
      const listaNova = deduplicar(ok.flat());
      setNovos(listaNova);

      setPaginaNovos((p) => ajustarPagina(listaNova, p, tamanhoPagina));
    } catch (err) {
      console.error("Erro ao carregar novos:", err);
    } finally {
      setLoadingNovos(false);
    }
  };

  const safeGetMinhas = async (path, label) => {
    try {
      const r = await axios.get(`${API_BASE_URL}${path}`, {
        headers: authHeader,
      });
      const lista = Array.isArray(r.data) ? r.data : [];
      const data = lista.map((it) => ({ ...it, _origem: label }));
      return { ok: true, label, data };
    } catch (err) {
      const status = err?.response?.status;
      const data = err?.response?.data;
      console.error(
        `[carregarMinhas] ${label} (${path}) falhou: status=${status}`,
        data || err?.message,
      );
      return { ok: false, label, status, data };
    }
  };

  const carregarMinhas = async () => {
    setLoadingMinhas(true);
    try {
      const [tca, tce, tcaEmp] = await Promise.all([
        safeGetMinhas("/tca/validacoes/minhas", "TCA"),
        safeGetMinhas("/tce/validacoes/minhas", "TCE"),
        safeGetMinhas("/tca-empregador/validacoes/minhas", "TCA-Empregador"),
      ]);

      const falhas = [tca, tce, tcaEmp].filter((r) => !r.ok);
      if (falhas.length) {
        console.error(
          "[carregarMinhas] Endpoints com falha:",
          falhas.map((f) => `${f.label}=${f.status}`).join(", "),
        );
      }

      const ok = [tca, tce, tcaEmp].filter((r) => r.ok).map((r) => r.data);
      const listaNova = deduplicar(ok.flat());
      setMinhas(listaNova);
      setPaginaMinhas((p) => ajustarPagina(listaNova, p, tamanhoPagina));
    } catch (err) {
      console.error("Erro ao carregar minhas:", err);
    } finally {
      setLoadingMinhas(false);
    }
  };

  useEffect(() => {
    if (!verificarPermissao()) {
      navigate("/dashboard", { replace: true });
      return;
    }
    carregarNovos();
    carregarMinhas();
    const interval = setInterval(() => {
      carregarNovos();
      carregarMinhas();
    }, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const atribuir = async (item) => {
    try {
      await axios.post(
        `${API_BASE_URL}${endpointAtribuir(item)}`,
        {},
        { headers: authHeader },
      );
      toastSuccess("Documento atribuído para você!");
      carregarNovos();
      carregarMinhas();
    } catch (err) {
      toastError(err.response?.data?.erro || "Erro ao atribuir documento.");
    }
  };

  const liberar = async (item) => {
    const ok = await confirm({
      title: "Liberar documento",
      message:
        "Liberar este documento de volta para a fila?\nOutro operador poderá atribuí-lo.",
      confirmText: "Sim, liberar",
      cancelText: "Cancelar",
      variant: "danger",
    });
    if (!ok) return;
    try {
      await axios.post(
        `${API_BASE_URL}${endpointLiberar(item)}`,
        {},
        { headers: authHeader },
      );
      toastSuccess("Documento liberado de volta para a fila.");
      carregarNovos();
      carregarMinhas();
    } catch (err) {
      toastError(err.response?.data?.erro || "Erro ao liberar documento.");
    }
  };

  const numeroDocumento = (item) => {
    const est = item.estudante_id || "N/A";
    const emp = item.empresa_id || "N/A";
    return `${est}-${emp}`;
  };

  const renderTabela = (lista, loading, acao, pagina, setPagina) => {
    if (loading) return <p className="val-loading">Carregando...</p>;
    if (lista.length === 0) {
      return (
        <p className="val-empty">
          {acao === "novos"
            ? "Nenhum documento aguardando atribuição."
            : "Você não tem documentos atribuídos."}
        </p>
      );
    }

    const { itens, totalPaginas, total, paginaSegura } = recortarPagina(
      lista,
      pagina,
      tamanhoPagina,
    );
    const inicio = (paginaSegura - 1) * tamanhoPagina + 1;
    const fim = inicio + itens.length - 1;

    // Janela de páginas com elipse — só monta o array.
    const paginasVisiveis = (() => {
      const arr = [];
      const max = totalPaginas;
      const p = paginaSegura;
      if (max <= 7) {
        for (let i = 1; i <= max; i++) arr.push(i);
      } else {
        arr.push(1);
        if (p > 4) arr.push("...");
        const inicioJanela = Math.max(2, p - 1);
        const fimJanela = Math.min(max - 1, p + 1);
        for (let i = inicioJanela; i <= fimJanela; i++) arr.push(i);
        if (p < max - 3) arr.push("...");
        arr.push(max);
      }
      return arr;
    })();

    return (
      <>
        <div className="val-table-meta">
          <span className="val-table-info">
            Mostrando <strong>{inicio}</strong>–<strong>{fim}</strong> de{" "}
            <strong>{total}</strong> documentos
          </span>
          <label className="val-pagesize">
            Por página:{" "}
            <select
              value={tamanhoPagina}
              onChange={(e) => {
                const novo = Number(e.target.value) || 10;
                setTamanhoPagina(novo);
                setPagina(1);
              }}
            >
              {PAGE_SIZE_OPTIONS.map((n) => (
                <option key={n} value={n}>
                  {n}
                </option>
              ))}
            </select>
          </label>
        </div>
        <table className="val-table">
          <thead>
            <tr>
              <th>Nº Documento</th>
              <th>CNPJ/ID</th>
              <th>Empresa</th>
              <th>Estudante</th>
              <th>Tipo</th>
              <th>Início Vigência</th>
              <th>Encaminhado em</th>
              <th>{acao === "novos" ? "Entrada na fila" : "Atribuído em"}</th>
              <th>Status</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {itens.map((item) => {
              const key = `${tipoDoc(item)}-${item.id}`;
              return (
                <tr key={key}>
                  <td>{numeroDocumento(item)}</td>
                  <td>{formatarCnpjCpf(item.empresa?.cnpj)}</td>
                  <td>{item.empresa?.razaosocial || "-"}</td>
                  <td>
                    {item.nome_aprendiz ||
                      item.nome_completo ||
                      item.nome_estagiario ||
                      "-"}
                  </td>
                  <td>
                    <span className="val-tipo-badge">{tipoDoc(item)}</span>
                  </td>
                  <td>
                    {item.data_inicio_contrato
                      ? formatarDataApenas(
                          toDateInputValue(item.data_inicio_contrato),
                        )
                      : "-"}
                  </td>
                  <td>{formatarDataHoraCurta(dataConclusaoItem(item))}</td>
                  <td>
                    {acao === "novos"
                      ? formatarDataHoraBR(item.created_at)
                      : formatarDataHoraBR(item.atribuido_em)}
                  </td>
                  <td>
                    <span
                      className={`val-status-badge val-status-${(
                        item.status || "PENDENTE"
                      ).toLowerCase()}`}
                    >
                      {item.status || "PENDENTE"}
                    </span>
                  </td>
                  <td className="val-acoes">
                    {acao === "novos" ? (
                      <button
                        className="val-btn-icon val-btn-attr"
                        onClick={() => atribuir(item)}
                        title="Atribuir para mim"
                      >
                        <FaHandPointer />
                      </button>
                    ) : (
                      <>
                        <button
                          className="val-btn-icon val-btn-view"
                          onClick={() => navigate(rotaDoc(item))}
                          title="Abrir documento"
                        >
                          <FaEye />
                        </button>
                        <button
                          className="val-btn-icon val-btn-lib"
                          onClick={() => liberar(item)}
                          title="Liberar"
                        >
                          <FaHandshake />
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <nav className="val-paginacao" aria-label="Paginação">
          <button
            className="val-page-btn"
            onClick={() => setPagina(paginaSegura - 1)}
            disabled={paginaSegura <= 1}
            title="Página anterior"
          >
            ‹ Anterior
          </button>
          {paginasVisiveis.map((p, i) =>
            p === "..." ? (
              <span key={`el-${i}`} className="val-page-ellipsis">
                …
              </span>
            ) : (
              <button
                key={p}
                className={`val-page-btn ${p === paginaSegura ? "val-page-ativa" : ""}`}
                onClick={() => setPagina(p)}
                aria-current={p === paginaSegura ? "page" : undefined}
              >
                {p}
              </button>
            ),
          )}
          <button
            className="val-page-btn"
            onClick={() => setPagina(paginaSegura + 1)}
            disabled={paginaSegura >= totalPaginas}
            title="Próxima página"
          >
            Próxima ›
          </button>
        </nav>
      </>
    );
  };

  if (!verificarPermissao()) return null;

  return (
    <div className="val-container">
      <header className="val-header">
        <div className="val-header-titulo">
          <FaClipboardCheck size={26} />
          <h1>Validações — Fila de Documentos</h1>
        </div>
        <button
          className="val-btn val-btn-secondary"
          onClick={() => navigate("/dashboard")}
        >
          <FaArrowLeft /> Voltar
        </button>
      </header>

      <div className="val-tabs">
        <button
          className={`val-tab ${abaAtiva === "novos" ? "val-tab-ativa" : ""}`}
          onClick={() => setAbaAtiva("novos")}
        >
          Novos
          {novos.length > 0 && (
            <span className="val-tab-badge">{novos.length}</span>
          )}
        </button>
        <button
          className={`val-tab ${abaAtiva === "minhas" ? "val-tab-ativa" : ""}`}
          onClick={() => setAbaAtiva("minhas")}
        >
          Minhas Validações
          {minhas.length > 0 && (
            <span className="val-tab-badge val-tab-badge-minhas">
              {minhas.length}
            </span>
          )}
        </button>
      </div>

      {abaAtiva === "novos" && (
        <section className="val-section">
          <h2>Documentos Aguardando Atribuição</h2>
          {renderTabela(novos, loadingNovos, "novos", paginaNovos, setPaginaNovos)}
        </section>
      )}

      {abaAtiva === "minhas" && (
        <section className="val-section">
          <h2>Meus Documentos em Validação</h2>
          {renderTabela(minhas, loadingMinhas, "minhas", paginaMinhas, setPaginaMinhas)}
        </section>
      )}

      <ToastContainer toasts={toasts} onRemove={removeToast} />
      {confirmDialog}
    </div>
  );
};

export default Validacoes;
